import React, { createContext, useState, useContext, useEffect, useCallback, useRef, useMemo } from 'react';
import { doc, getDocFromServer } from 'firebase/firestore';
import { saveUserProfile } from '../auth/profile.mjs';
import { useFirebase } from './FirebaseContext';

const UserContext = createContext();
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) throw new Error('useUser must be used within a UserProvider');
  return context;
};

// Retain the existing completion criterion so older, valid profiles still work.
const profileStatus = profile => !profile ? 'missing' :
  (String(profile.firstName || '').trim() ? 'ready' : 'incomplete');
const emptyProfile = { uid: null, status: 'idle', data: null, error: null };

export const UserProvider = ({ children }) => {
  const { user, auth, firestore, loading, logout } = useFirebase();
  const [profile, setProfile] = useState(emptyProfile);
  const request = useRef(0);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; request.current += 1; };
  }, []);

  const refreshUserData = useCallback(async () => {
    const version = ++request.current;
    if (!user) {
      setProfile(emptyProfile);
      return;
    }
    if (user.isAnonymous) {
      setProfile({ uid: user.uid, status: 'guest', data: null, error: null });
      return;
    }
    setProfile({ uid: user.uid, status: 'loading', data: null, error: null });
    const isCurrent = () => mounted.current && version === request.current &&
      auth.currentUser?.uid === user.uid;
    try {
      // A failed/offline lookup is never evidence that a profile is missing.
      const snapshot = await getDocFromServer(doc(firestore, 'users', user.uid));
      const data = snapshot.exists() ? snapshot.data() : null;
      if (isCurrent()) setProfile({ uid: user.uid, status: profileStatus(data), data, error: null });
    } catch (error) {
      if (isCurrent()) setProfile({ uid: user.uid, status: 'error', data: null, error });
    }
  }, [user, auth, firestore]);

  useEffect(() => {
    refreshUserData();
    return () => { request.current += 1; };
  }, [refreshUserData]);

  const saveProfile = async (changes, account, completing = false) => {
    const merged = await saveUserProfile({ auth, firestore, account, changes, completing });
    request.current += 1; // Ignore any older profile lookup still in flight.
    if (mounted.current) setProfile({ uid: account.uid, status: profileStatus(merged), data: merged, error: null });
    return merged;
  };

  // Suppress a previous account's data immediately, before the loading effect runs.
  const status = loading || (user && profile.uid !== user.uid) ? 'loading' :
    !user ? 'signed-out' : profile.status;
  const data = user && profile.uid === user.uid ? profile.data : null;
  const userData = useMemo(() => ({
    ...data,
    id: user?.uid || null,
    email: user?.email || '',
    name: data?.name || [data?.firstName, data?.lastName].filter(Boolean).join(' ') ||
      user?.displayName || (user?.isAnonymous ? 'Guest User' : 'User'),
    role: data?.role || data?.practitionerType || 'student',
    isLoading: status === 'loading',
    isAuthenticated: !!user,
    profileImage: data?.profileImage || null
  }), [data, user, status]);

  return <UserContext.Provider value={{
    userData, profile: data, profileStatus: status,
    profileError: status === 'error' ? profile.error : null,
    refreshUserData, logout,
    completeProfile: (data, account = user) => saveProfile(data, account, true),
    updateUserData: data => saveProfile(data, user)
  }}>{children}</UserContext.Provider>;
};

export default UserProvider;
